import React from "react";

/**
 * DiffPanel — field-by-field side-by-side diff between two rule-template
 * snapshots (older vs. newer/current). Extracted from RuleMarketplace so
 * the marketplace file stays focused on browse/manage flows.
 *
 * Props:
 *   older  : snapshot { version, name, description, input, value, action, creative, category }
 *   newer  : snapshot with the same shape (typically the "current" snapshot)
 *   onBack : callback to return to the version list
 *   envs   : optional array of saved test envs {id, name, env} — when
 *            present, we auto-replay each env against BOTH snapshots and
 *            flag any that changed firing status (drift). Owner-only data,
 *            never fetched for public template views.
 */

const DIFF_FIELDS = [
  { k: "name", label: "Name" },
  { k: "description", label: "Description" },
  { k: "input", label: "Input" },
  { k: "value", label: "Value" },
  { k: "action", label: "Action" },
  { k: "creative", label: "Creative" },
  { k: "category", label: "Category" },
];

// A rule fires when the env's value at the rule's input axis matches the
// rule's `value` field (case-insensitive). Copied verbatim from the live
// simulator so behaviour stays consistent.
function firesFor(snap, env) {
  if (!snap || !env) return false;
  const axis = env[snap.input];
  if (axis == null) return false;
  return String(axis).toLowerCase() === String(snap.value || "").toLowerCase();
}

export default function DiffPanel({ older, newer, onBack, envs }) {
  const replay = React.useMemo(() => {
    if (!Array.isArray(envs) || envs.length === 0) return [];
    return envs.map((e) => {
      const before = firesFor(older, e.env);
      const after = firesFor(newer, e.env);
      return {
        ...e,
        before,
        after,
        drifted: before !== after,
      };
    });
  }, [envs, older, newer]);

  const drifts = replay.filter((r) => r.drifted).length;

  return (
    <div data-testid="rt-history-diff-panel" className="space-y-3">
      <button
        onClick={onBack}
        data-testid="rt-history-diff-back"
        className="text-[10px] uppercase tracking-[0.22em] text-white/50 hover:text-white"
      >
        ← Back to versions
      </button>
      <div className="grid grid-cols-[80px_1fr_1fr] gap-px bg-white/10 border border-white/10 text-xs">
        <div className="p-2 bg-black text-white/40 text-[9px] uppercase tracking-[0.2em]">
          Field
        </div>
        <div className="p-2 bg-black text-white/60 text-[10px] uppercase tracking-[0.18em]">
          v{older.version}
        </div>
        <div className="p-2 bg-black text-[#E8D6A3] text-[10px] uppercase tracking-[0.18em]">
          Current
        </div>
        {DIFF_FIELDS.map((f) => {
          const a = String(older[f.k] ?? "");
          const b = String(newer[f.k] ?? "");
          const changed = a !== b;
          return (
            <React.Fragment key={f.k}>
              <div className="p-2 bg-[#0A0908] text-white/50 text-[10px] uppercase tracking-[0.18em]">
                {f.label}
              </div>
              <div
                data-testid={`rt-diff-${f.k}-a`}
                className={`p-2 font-mono text-[11px] ${
                  changed ? "bg-red-500/10 text-red-300" : "bg-[#0A0908] text-white/70"
                }`}
              >
                {a || <span className="text-white/30">—</span>}
              </div>
              <div
                data-testid={`rt-diff-${f.k}-b`}
                className={`p-2 font-mono text-[11px] ${
                  changed ? "bg-emerald-500/10 text-emerald-300" : "bg-[#0A0908] text-white/70"
                }`}
              >
                {b || <span className="text-white/30">—</span>}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Env replay — shows if any saved test env changed firing status. */}
      {replay.length > 0 && (
        <div
          data-testid="rt-diff-env-replay"
          className="border border-white/10 rounded overflow-hidden"
        >
          <div className="flex items-center justify-between px-3 py-2 bg-black text-[10px] uppercase tracking-[0.22em]">
            <span className="text-white/60">Saved env replay</span>
            <span
              data-testid="rt-diff-drift-count"
              className={
                drifts > 0
                  ? "text-amber-400 font-semibold"
                  : "text-emerald-300 font-semibold"
              }
            >
              {drifts > 0
                ? `${drifts} drifted`
                : "no drift"}
            </span>
          </div>
          <div className="divide-y divide-white/10">
            {replay.map((r) => (
              <div
                key={r.id}
                data-testid={`rt-diff-env-${r.id}`}
                className={`px-3 py-2 flex items-center gap-3 text-[11px] ${
                  r.drifted ? "bg-amber-500/[0.05]" : "bg-[#0A0908]"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-white/85 font-medium truncate">
                    {r.name}
                  </div>
                  <div className="text-[10px] font-mono text-white/40 truncate">
                    {Object.entries(r.env)
                      .map(([k, v]) => `${k}=${v}`)
                      .join(" · ")}
                  </div>
                </div>
                <div
                  data-testid={`rt-diff-env-before-${r.id}`}
                  className={`px-2 py-0.5 rounded font-mono text-[10px] ${
                    r.before
                      ? "bg-emerald-500/15 text-emerald-300"
                      : "bg-white/10 text-white/50"
                  }`}
                >
                  v{older.version}: {r.before ? "firing" : "idle"}
                </div>
                <span className="text-white/30">→</span>
                <div
                  data-testid={`rt-diff-env-after-${r.id}`}
                  className={`px-2 py-0.5 rounded font-mono text-[10px] ${
                    r.after
                      ? "bg-emerald-500/15 text-emerald-300"
                      : "bg-white/10 text-white/50"
                  }`}
                >
                  current: {r.after ? "firing" : "idle"}
                </div>
                {r.drifted && (
                  <span
                    data-testid={`rt-diff-env-drift-${r.id}`}
                    className="text-[9px] uppercase tracking-[0.18em] text-amber-400 font-semibold ml-1"
                  >
                    drift
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
